import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';

class StateManager {
    constructor() {
        this.state = {
            viewMode: 'tiles', // 'tiles', 'list'
            mapMode: 'roadmap', // 'roadmap', 'satellite'
            currentPage: 'maps',
            mobileMenuOpen: false,
            mapPosition: {
                lat: 45.5017,
                lng: -122.6750,
                zoom: 10
            },
            selectedVessel: null,
            filters: {
                vesselType: 'all',
                status: 'all'
            }
        };
        
        this.listeners = new Map();
        this.userId = null;
        this.db = null;
        
        // Load initial state from localStorage
        this.loadFromLocalStorage();
    }

    // Initialize with user authentication
    async initialize(user, firestore) {
        this.userId = user?.uid;
        this.db = firestore;
        
        if (this.userId) {
            await this.loadFromFirestore();
        }
    }

    // Subscribe to state changes
    subscribe(key, callback) {
        if (!this.listeners.has(key)) {
            this.listeners.set(key, []);
        }
        this.listeners.get(key).push(callback);
        
        // Return unsubscribe function
        return () => {
            const callbacks = this.listeners.get(key);
            if (callbacks) {
                const index = callbacks.indexOf(callback);
                if (index > -1) {
                    callbacks.splice(index, 1);
                }
            }
        };
    }

    // Notify listeners of state changes
    notify(key, value) {
        const callbacks = this.listeners.get(key);
        if (callbacks) {
            callbacks.forEach(callback => callback(value, this.state));
        }
    }

    // Update state
    setState(updates) {
        const previousState = { ...this.state };
        
        Object.keys(updates).forEach(key => {
            if (this.state.hasOwnProperty(key)) {
                this.state[key] = updates[key];
                this.notify(key, this.state[key]);
            }
        });

        // Save to storage
        this.saveToLocalStorage();
        if (this.userId && this.db) {
            this.saveToFirestore();
        }
    }

    // Get current state
    getState(key = null) {
        return key ? this.state[key] : { ...this.state };
    }

    // Save to localStorage
    saveToLocalStorage() {
        try {
            localStorage.setItem('mdm-dashboard-state', JSON.stringify(this.state));
        } catch (error) {
            console.warn('Failed to save state to localStorage:', error);
        }
    }

    // Load from localStorage
    loadFromLocalStorage() {
        try {
            const saved = localStorage.getItem('mdm-dashboard-state');
            if (saved) {
                const parsedState = JSON.parse(saved);
                this.state = { ...this.state, ...parsedState };
            }
        } catch (error) {
            console.warn('Failed to load state from localStorage:', error);
        }
    }

    // Save to Firestore
    async saveToFirestore() {
        if (!this.userId || !this.db) return;
        
        try {
            const userPrefsRef = doc(this.db, 'userPreferences', this.userId);
            await setDoc(userPrefsRef, {
                ...this.state,
                lastUpdated: new Date()
            }, { merge: true });
        } catch (error) {
            console.warn('Failed to save state to Firestore:', error);
        }
    }

    // Load from Firestore
    async loadFromFirestore() {
        if (!this.userId || !this.db) return;
        
        try {
            const userPrefsRef = doc(this.db, 'userPreferences', this.userId);
            const docSnap = await getDoc(userPrefsRef);
            
            if (docSnap.exists()) {
                const data = docSnap.data();
                delete data.lastUpdated; // Remove metadata
                this.state = { ...this.state, ...data };
                
                // Notify all listeners of the loaded state
                Object.keys(this.state).forEach(key => {
                    this.notify(key, this.state[key]);
                });
            }
        } catch (error) {
            console.warn('Failed to load state from Firestore:', error);
        }
    }
}

// Create singleton instance
export const stateManager = new StateManager();
export default stateManager;